import type { MeshStandardMaterial } from 'three/src/materials/MeshStandardMaterial';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module';
import { type GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { PerspectiveCamera } from 'three/src/cameras/PerspectiveCamera';
import type { AnimationClip } from 'three/src/animation/AnimationClip';
import { LinearToneMapping, sRGBEncoding } from 'three/src/constants';
import { DirectionalLight } from 'three/src/lights/DirectionalLight';
import { AnimationMixer } from 'three/src/animation/AnimationMixer';

import { WebGLRenderer } from 'three/src/renderers/WebGLRenderer';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils';
import { AmbientLight } from 'three/src/lights/AmbientLight';
import type { ColorRepresentation } from 'three/src/utils';
import type { Object3D } from 'three/src/core/Object3D';
import type { AvatarData } from '@/services/api/types';
import type { Mesh } from 'three/src/objects/Mesh';

import { LoopRepeat } from 'three/src/constants';
import { Vector3 } from 'three/src/math/Vector3';
import { Scene } from 'three/src/scenes/Scene';
import { Clock } from 'three/src/core/Clock';
import { Color } from 'three/src/math/Color';
import Positions from './positions';
import RAF from '@/utils/RAF';

export default class OverviewScene {
  public onLoad?: () => void;
  private expression?: string;

  private renderer!: WebGLRenderer;
  private readonly baseScale = 0.045;
  private camera!: PerspectiveCamera;

  private readonly clock = new Clock();
  private readonly scene = new Scene();

  private readonly origin = new Vector3();
  private readonly avatars: Object3D[] = [];
  private readonly loader = new GLTFLoader();

  private readonly customMaterialName = 'Custom';
  private readonly mixers: AnimationMixer[] = [];
  private readonly update = this.render.bind(this);
  private readonly baseChildren = ['BODY', 'mixamorig_Hips_2'];

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly baseModelUrl: string,
    avatars: AvatarData[]
  ) {
    this.loader.setMeshoptDecoder(MeshoptDecoder);

    this.createRenderer(innerWidth, innerHeight);
    this.createCamera(innerWidth / innerHeight);

    this.createAvatars(avatars);
    this.createLights();
  }

  private async createAvatars(avatars: AvatarData[]): Promise<void> {
    const baseModelUrl = avatars[0]?.baseModelUrl ?? this.baseModelUrl;
    const baseModel = await this.loadBaseModel(baseModelUrl);
    const positions = Positions.get(avatars.length);

    const idle = baseModel.animations.find(
      ({ name }) => name === 'IDLE'
    ) as AnimationClip;

    avatars.forEach((avatar, a) => {
      const position = positions?.[a] ?? this.origin;
      this.setAvatar(avatar, clone(baseModel.scene), position, idle);
    });

    RAF.add(this.update);
    RAF.pause = false;
    this.onLoad?.();
  }

  private setMouthColor(mouth: Mesh, color: number, alpha: number): void {
    const material = mouth.material as MeshStandardMaterial;
    material.emissiveIntensity = alpha;
    material.emissive.set(color);
  }

  private createRenderer(width: number, height: number): void {
    this.renderer = new WebGLRenderer({
      powerPreference: 'high-performance',
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });

    this.renderer.debug.checkShaderErrors = import.meta.env.DEV;
    this.renderer.setPixelRatio(devicePixelRatio || 1.0);
    this.renderer.setClearColor(Color.NAMES.black, 0.0);

    this.renderer.toneMapping = LinearToneMapping;
    this.renderer.outputEncoding = sRGBEncoding;
    this.renderer.setSize(width, height);
  }

  private loadBaseModel(baseModelUrl: string): Promise<GLTF> {
    return this.loader.loadAsync(baseModelUrl, this.onProgress.bind(this));
  }

  private onProgress(event: ProgressEvent): void {
    const progress = ~~((10.0 * (event.loaded * 100.0)) / event.total);
    !TEST && console.info(`Loading... ${progress / 10.0}%`);
  }

  private createCamera(ratio: number): void {
    this.camera = new PerspectiveCamera(50, ratio, 50, 150);
    this.camera.position.set(0.0, 20.0, 50.0);
  }

  private updateElementMaterial(
    element: Object3D,
    color?: ColorRepresentation
  ): void {
    const mesh = element as Mesh;
    if (!mesh.material) return;

    let material = mesh.material as MeshStandardMaterial;
    if (!material.name.includes(this.customMaterialName)) return;

    color ??= material.color.clone();
    material = material.clone();

    material.color.set(color).convertSRGBToLinear();
    mesh.material = material;
  }

  private createLights(): void {
    const right = new DirectionalLight(Color.NAMES.white, 0.67);
    const left = new DirectionalLight(Color.NAMES.white, 0.33);
    const ambient = new AmbientLight(Color.NAMES.white, 0.5);

    left.position.set(-100.0, 100.0, 100.0);
    right.position.setScalar(100.0);

    right.lookAt(0.0, 0.0, 0.0);
    left.lookAt(0.0, 0.0, 0.0);

    this.scene.add(ambient);
    this.scene.add(right);
    this.scene.add(left);
  }

  private render(): void {
    const delta = this.clock.getDelta();

    for (let m = this.mixers.length; m--; ) {
      this.mixers[m].update(delta);
    }

    this.renderer.render(this.scene, this.camera);
  }

  public dispose(): void {
    RAF.remove(this.update);
    this.mixers.length = 0.0;

    this.renderer.domElement.remove();
    this.renderer.dispose();
    this.scene.clear();
  }

  private setAvatar(
    data: AvatarData,
    avatar: Object3D,
    position: Vector3,
    animation: AnimationClip
  ): void {
    const { children } = avatar.children[0];
    const { id, age, skinColor, elements = [] } = data;

    this.expression = elements.find(({ name }) =>
      name.includes('Expression')
    )?.name;

    for (let child = children.length; child--; ) {
      const { name: elementName } = children[child];

      if (this.baseChildren.includes(elementName)) {
        elementName === this.baseChildren[0] &&
          children[child].traverse((child) =>
            this.updateElementMaterial(child, skinColor)
          );

        continue;
      }

      if (this.expression === elementName) {
        const mouth = children[child].getObjectByName(this.expression);

        this.setMouthColor(mouth?.children[0] as Mesh, 0x810204, 0.55);
        this.updateElementMaterial(mouth?.children[1] as Object3D, skinColor);

        continue;
      }

      const element = elements.find(({ name }) => name === elementName);

      if (!element) {
        children[child].removeFromParent();
        continue;
      }

      children[child].traverse((child) =>
        this.updateElementMaterial(child, element.color)
      );
    }

    const scale = age === 'Child' ? 90.0 : age === 'Teen' ? 95.0 : 100.0;
    const delay = Math.random() * animation.duration + 0.1;
    avatar.scale.setScalar(this.baseScale * scale);

    const mixer = new AnimationMixer(avatar);
    const action = mixer.clipAction(animation);

    action.setLoop(LoopRepeat, Infinity);
    avatar.position.copy(position);

    this.avatars.push(avatar);
    avatar.userData.id = id;
    this.scene.add(avatar);

    action.play();
    mixer.setTime(delay);
    this.mixers.push(mixer);
  }

  public removeAvatar(id: string): void {
    const avatar = this.avatars.find(
      ({ userData }) => userData.id === id
    ) as Object3D;

    const positions = Positions.get(this.avatars.length - 1);
    const avatarIndex = this.avatars.indexOf(avatar);

    this.avatars.splice(avatarIndex, 1);
    this.scene.remove(avatar);

    this.avatars.forEach((avatar, a) => {
      const position = positions?.[a] ?? this.origin;
      avatar.position.copy(position);
    });
  }
}
